import { EmptyState } from "#/components/ui/EmptyState";
import { useClient } from "#/hook/useClient";
import { formatDate } from "#/utils/dateTimeUtils";
import PrvTargetBadge from "./PrvTargetBadge";
import { formatDuration, type TargetCheck } from "./prvUtils";
import { withBoldValues } from "./prvText";
import { usePrvTranslation } from "./usePrvTranslation";

/** One category's aggregated numbers for the selected scope. */
export interface PrvCategoryCard {
  productId: number;
  name: string;
  color: string | null;
  /** Items in a recorded, still open stay at the sub-client(s). */
  present: number;
  /** Arrivals = stays that passed min dwell, open or ended. */
  arrivals: number;
  /** Departures = arrivals that have ended. */
  departures: number;
  /** Average time from arrival to first scan elsewhere, over the departures. */
  averageStaySeconds: number | null;
  /** Arrivals per `days`, over the observed span; null when there is no arrival yet. */
  rate: { arrivals: number; days: number } | null;
  firstMovementAt: string | null;
  lastMovementAt: string | null;
  /** The pair's rotation target checked against its latest interval; null when none is defined. */
  target: TargetCheck | null;
}

interface Props {
  cards: PrvCategoryCard[];
  selectedProductId: number | null;
  onSelect: (productId: number) => void;
  /**
   * Rotation targets belong to a sub-client, so the target line only makes
   * sense when the cards are scoped to one. False in the "all sub-clients" view.
   */
  showTarget: boolean;
  /** End of the latest interval for the target check: the last run's start. */
  asOf: string;
}

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs tracking-wide text-gray-500 uppercase">{label}</span>
    <span className="text-lg leading-tight font-semibold text-gray-900 tabular-nums">{value}</span>
  </div>
);

/**
 * One card per category. Reads top to bottom as the four facts the engine
 * has: how many are there now, how many arrived, how many left, how long they
 * stayed — with the period the counts cover stated right under them.
 */
const PrvCategoryCards = ({ cards, selectedProductId, onSelect, showTarget, asOf }: Props) => {
  const t = usePrvTranslation();
  const { client } = useClient();

  if (cards.length === 0) {
    return <EmptyState title={t("clients.prv.results.cards.empty")} />;
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {cards.map((card) => {
        const selected = selectedProductId === card.productId;
        // With a selection, the other cards fade but stay readable on hover and clickable.
        const dimmed = selectedProductId !== null && !selected;
        return (
          <button
            key={card.productId}
            type="button"
            aria-pressed={selected}
            onClick={() => onSelect(card.productId)}
            className={`flex flex-col gap-3 rounded-xl border bg-white px-4 py-3 text-left transition hover:border-gray-400 ${
              selected ? "border-gray-900 shadow-sm" : "border-gray-200"
            } ${dimmed ? "opacity-30 hover:opacity-100" : ""}`}
          >
            {/* Title row: category on the left, items there now on the right */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: card.color ?? "#9ca3af" }}
                />
                <span className="truncate text-sm font-semibold text-gray-900" title={card.name}>
                  {card.name}
                </span>
              </div>
              <div className="flex shrink-0 items-baseline gap-1.5">
                <span className="text-2xl leading-none font-bold text-gray-900 tabular-nums">
                  {card.present}
                </span>
                <span className="text-xs text-gray-600">
                  {t("clients.prv.results.cards.presentNow")}
                </span>
              </div>
            </div>

            {/* Body: the three counts and the period they cover */}
            <div className="flex flex-col gap-2 border-y border-gray-200 py-3">
              <div className="grid grid-cols-3 gap-3">
                <Stat
                  label={t("clients.prv.results.cards.arrivals")}
                  value={String(card.arrivals)}
                />
                <Stat
                  label={t("clients.prv.results.cards.departures")}
                  value={String(card.departures)}
                />
                <Stat
                  label={t("clients.prv.results.cards.avgTime")}
                  value={formatDuration(card.averageStaySeconds, t)}
                />
              </div>
              <div className="text-sm text-gray-600">
                {card.rate && card.firstMovementAt ? (
                  <span>
                    {t("clients.prv.results.cards.since")}{" "}
                    <span className="font-semibold text-gray-900">
                      {formatDate(card.firstMovementAt, client)}
                    </span>
                    {" · "}
                    {withBoldValues(t, "clients.prv.results.cards.rateLine", {
                      rate: card.rate.arrivals.toFixed(1),
                      days: card.rate.days,
                    })}
                  </span>
                ) : (
                  <span>{t("clients.prv.results.cards.noActivity")}</span>
                )}
              </div>
            </div>

            {/* Footer: rotation target (one sub-client only) left, last activity right */}
            <div className="flex items-center justify-between gap-4 text-sm text-gray-600">
              {showTarget && <PrvTargetBadge check={card.target} asOf={asOf} />}
              {card.lastMovementAt && (
                <span className={showTarget ? "text-right" : ""}>
                  {t("clients.prv.results.cards.lastActivity")}{" "}
                  <span className="font-semibold text-gray-900">
                    {formatDate(card.lastMovementAt, client)}
                  </span>
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default PrvCategoryCards;
